import type { Config } from "@phn/entities/config";
import {
  Component,
  Event,
  EventEmitter,
  h,
  Listen,
  Prop,
  setErrorHandler,
  State,
  Watch,
} from "@stencil/core";
import logger from "@phn/services/logger";
import featureToggles from "@phn/services/feature-toggles";
import { getEnvironment } from "@phn/entities/environment";
import { GlobalDataLayerService } from "@phn/services/analytics/global-data-layer-service";
import * as contentService from "@phn/services/content-service";
import {
  getTokenSilently,
  initializeUserData,
} from "@phn/services/user-service";
import windowService from "@phn/services/window-service";
import { navContentStore } from "@phn/state/nav-content-store";
import { navStateStore } from "@phn/state/nav-state-store";
import {
  shopStateStore,
  ShopStateStoreTypes,
} from "@phn/state/shop-state-store";
import { routerStore } from "@phn/state/router-store";
import {
  DEFAULT_LOCALE,
  NavigationMode,
  NavigationModeType,
  NavigationTheme,
  NavigationThemeType,
} from "@phn/utility/constants";
import { sanitizeApp } from "@phn/utility/navi-state";
import {
  isShopAvailable,
  loadsShopPropsOnStore,
} from "@phn/utility/helper/shop-helper";
import { Timeout } from "@phn/utility/timeout";
import { getConfig, isString } from "@phn/utility/helper";
import { Application, ApplicationType } from "@phn/utility/constants/app";
import * as headerService from "@phn/services/header-service";
import { featureToggleStateStore } from "@phn/state/feature-toggles-state";
import { initErrorQueue } from "@phn/services/error-queue.service";
import { navigationAgentErrorHandler } from "@phn/services/new-relic-service";
import type { NavigateAuthOptions } from "@phn/entities/navigate-auth";
import { NavigationDrawerScripts } from "./navigation-drawer-scripts";
import { Message } from "../../entities/auth";

@Component({
  tag: "phn-header",
  styleUrl: "header.scss",
  shadow: true,
  assetsDirs: ["auth"],
})
export class Header {
  @Prop() locale = DEFAULT_LOCALE;

  @Prop() env = "";

  @Prop() clientId = "";

  @Prop() pageName = "";

  @Prop() theme: NavigationThemeType = NavigationTheme.light;

  @Prop() mode: NavigationModeType = NavigationMode.navbar;

  @Prop() limitedWidth = "true";

  @Prop() app: ApplicationType = Application.default;

  @Prop() initialRoutingPath = "";

  @Prop() displayLogoOnly = false;

  @Prop() numberOfShoppingItems = 0;

  @Prop() wishlistItemsNumber = 0;

  @Prop() shopInformation =
    '{ "wishlistItemsNumber": 0, "numberOfShoppingItems": 0, "subTotal": "0" }';

  @Prop() authOptions? = "{}";

  @Prop() hideShowOnScroll? = false;

  @State() loadingContent = false;

  @State() receivedContent = false;

  @State() breakpoint = windowService.getBreakpoint();

  private eventPreventedTimeout = new Timeout(0);

  private loadedToggles = false;

  @Event({ composed: true }) navigationDidLoad!: EventEmitter<void>;

  @Watch("locale")
  async localeWatchHandler(): Promise<void> {
    navStateStore.state.locale = this.locale;
    await this.initNavContentStore();
    GlobalDataLayerService.getInstance().setLocale(this.locale);
  }

  @Watch("pageName")
  pageNameWatchHandler(): void {
    navStateStore.state.pageName = this.pageName;
    GlobalDataLayerService.getInstance().setPageName(this.pageName);
  }

  @Watch("env")
  async newEnvReceived(): Promise<void> {
    await this.initEnvironment();
    await this.initNavContentStore();

    getTokenSilently(getConfig(navStateStore.state.env));
  }

  @Watch("app")
  appWatcher(newValue: string): void {
    navStateStore.state.app = sanitizeApp(newValue);
  }

  @Watch("shopInformation")
  watchShopVariable(
    information: string,
    _: string,
    propName: keyof ShopStateStoreTypes,
  ): void {
    const shopInformation = JSON.parse(information);
    // eslint-disable-next-line
    shopStateStore.set(propName, shopInformation);
  }

  @Watch("initialRoutingPath")
  initialRoutingPathWatcher(newRoutingPath: string): void {
    navStateStore.state.initialRoutingPath = newRoutingPath;
  }

  @Watch("theme")
  themeWatcher(newValue: NavigationThemeType): void {
    navStateStore.state.theme = newValue;
  }

  @Watch("mode")
  modeWatcher(newValue: NavigationModeType): void {
    navStateStore.state.mode = newValue;
  }

  @Watch("authOptions")
  auth0Watcher(newValue: string): void {
    this.setAuthConfig(
      JSON.parse(newValue) as NavigateAuthOptions,
      getConfig(navStateStore.state.env),
    );
  }

  /* istanbul ignore next */
  @Listen("resize", { target: "window", passive: true })
  resizeHandler(): void {
    this.breakpoint = windowService.getBreakpoint();
    routerStore.state.breakpoint = windowService.getBreakpoint();
  }

  @Listen("getToken", { target: "window", passive: true })
  async newAuthMessageReceived(event: Message & CustomEvent): Promise<void> {
    if (isString(event.detail?.token)) {
      try {
        await initializeUserData(event.detail.token as string, this.locale);
      } catch (err) {
        logger.error("INIT_USER_DATA", err as Error);
      }
    }
  }

  @Listen("ucInit", { target: "window", passive: true })
  handleUserCentricsInitalization(): void {
    try {
      this.checkMarketingConsent();
    } catch (err) {
      logger.error("INIT_COOKIE_CONSENT", err as Error);
    }
  }

  @Listen("navigationLoaded")
  navigationLoadedHandler({
    detail: navigationLoaded,
  }: CustomEvent<boolean>): void {
    windowService.navigationLoaded = navigationLoaded;

    if (navigationLoaded) {
      this.navigationDidLoad.emit();
    }
  }

  private checkMarketingConsent(): void {
    windowService.maybeCheckMarketingConsent();
  }

  private async initEnvironment(): Promise<void> {
    navStateStore.state.env = getEnvironment(this.env);
    navStateStore.state.app = sanitizeApp(this.app);
    navStateStore.state.locale = this.locale;
    navStateStore.state.pageName = this.pageName;
    navStateStore.state.initialRoutingPath = this.initialRoutingPath;
    GlobalDataLayerService.getInstance().setEnvironment(
      navStateStore.state.env,
    );
    featureToggles.setEnvironment(navStateStore.state.env);
    navStateStore.state.theme = this.theme;
    navStateStore.state.mode = this.mode;

    const config = getConfig(navStateStore.state.env);
    this.setAuthConfig(
      JSON.parse(this.authOptions!) as NavigateAuthOptions,
      config,
    );
    getTokenSilently(config);

    this.loadedToggles = false;
    await featureToggles.setFeatures(navStateStore.state.env);
    featureToggleStateStore.state.features = featureToggles.features;
    this.loadedToggles = true;

    const shopInformation = JSON.parse(this.shopInformation);

    loadsShopPropsOnStore({
      shopInformation,
    });
  }

  private setAuthConfig(
    authOptions: NavigateAuthOptions,
    config: Config,
  ): void {
    if (authOptions.redirectUrl) {
      config.IDENTITY_REDIRECT_URI = authOptions.redirectUrl;
    }

    if (authOptions.logoutCallback) {
      config.IDENTITY_LOGOUT_CALLBACK = authOptions.logoutCallback;
    }

    navStateStore.state.authOptions = authOptions;
  }

  private async initNavContentStore(): Promise<void> {
    const previouslyReceivedContent = this.receivedContent;
    this.receivedContent = false;
    this.loadingContent = true;
    try {
      const fetchedLocale = this.locale;
      const fetchedEnv = navStateStore.state.env;

      const contentForLocale = await contentService.fetchContent(this.app);

      // This is to make sure that the response is valid for the current locale and environment
      // and wasn't made with another locale or enironment
      if (
        this.locale === fetchedLocale &&
        navStateStore.state.env === fetchedEnv
      ) {
        Object.assign(navContentStore.state, contentForLocale);
        this.receivedContent = true;
      }
    } catch (err) {
      logger.error("FETCH_CONTENT", err as Error);

      if (previouslyReceivedContent) {
        /* istanbul ignore next */
        this.receivedContent = true;
      }
    }

    this.loadingContent = false;
  }

  componentWillLoad(): void {
    initErrorQueue();
    setErrorHandler(navigationAgentErrorHandler);
    GlobalDataLayerService.getInstance().setDefaultProperties(
      this.locale,
      this.pageName,
    );
    (window as any).ncs = navContentStore;

    this.initEnvironment().then(async () => this.initNavContentStore());
    const style = document.createElement("style");
    style.textContent = `
     phn-header + * {
      isolation: isolate;
     }`;
    document.head.appendChild(style);
  }

  componentDidLoad(): void {
    try {
      this.checkMarketingConsent();
    } catch (err) {
      logger.error("INIT_COOKIE_CONSENT", err as Error);
    }

    headerService.onComponentDidLoad();
  }

  disconnectedCallback(): void {
    this.eventPreventedTimeout.cancel();
  }

  private hasLimitedWidth(): boolean {
    return !!(
      this.mode === NavigationMode.hero && this.limitedWidth === "true"
    );
  }

  render(): HTMLPhnHeaderElement {
    const { shop } = navContentStore.state;

    if (!this.loadedToggles) {
      return <div></div>;
    }

    return (
      // div needed because conditional styling does not work on Host of shadow component for some unknown reason
      <nav class={this.hasLimitedWidth() ? "header limited-width" : "header"}>
        <NavigationDrawerScripts />

        <phn-wrapper
          theme={this.theme}
          mode={this.mode}
          locale={this.locale}
          loadingContent={this.loadingContent}
          receivedContent={this.receivedContent}
          breakpoint={this.breakpoint}
          app={sanitizeApp(this.app)}
          displayLogoOnly={this.displayLogoOnly}
          limitedWidth={this.hasLimitedWidth()}
          hideShowOnScroll={this.hideShowOnScroll}
        >
          {isShopAvailable(shop, this.locale, sanitizeApp(this.app)) ? (
            <div slot="contextual_drawer_header">
              <slot name="contextual_drawer_header"></slot>
            </div>
          ) : null}
        </phn-wrapper>
      </nav>
    );
  }
}
